"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.convertSharePointToExcelRow = convertSharePointToExcelRow;
exports.convertSharePointItemsToExcelRows = convertSharePointItemsToExcelRows;
exports.getAvailableSharePointFields = getAvailableSharePointFields;
exports.getExcelFieldForSharePointField = getExcelFieldForSharePointField;
exports.getAllFieldMappings = getAllFieldMappings;
exports.validateSharePointItem = validateSharePointItem;
exports.logFieldMappingSummary = logFieldMappingSummary;
const logger_1 = require("../logger");
/**
 * Mapping configuration from SharePoint field names to Excel column names
 * This allows SharePoint data to be processed by the existing createBaseTrial function
 */
const sharePointToExcelMapping = {
    // Basic identification fields
    "Id": "Protocol Abbrev",
    "Title": "Title (full)",
    // Identification fields
    "IRB_x0020_Number": "IRB No.",
    "NCT_x0020_ID": "ClinicalTrials.gov ID",
    "Protocol_x0020_Number": "Secondary Protocol No.",
    // Description fields
    "Study_x0020_Objective": "Objective",
    "Phase": "Phase",
    "Disease_x0020_Site": "Condition",
    "Intervention": "Therapies Involved",
    "Treatment": "Treatment",
    // Eligibility fields
    "Age_x0020_Group": "Age Group",
    "Eligibility_x0020_Criteria": "Key Eligibility",
    // Contact and location fields
    "Principal_x0020_Investigator": "Principal Investigator",
    "Sub_x0020_Investigators": "Sub Investigators",
    "Contact_x0020_Name": "Contact",
    "Contact_x0020_Phone": "Phone",
    // Service and organizational fields
    "Service_x0020_Line": "Service Line",
    "Section": "Section",
    // Status fields
    "Study_x0020_Status": "Study Status",
    // Additional fields that might be present
    "Condition": "Condition",
    "Therapies_x0020_Involved": "Therapies Involved",
    "Key_x0020_Eligibility": "Key Eligibility",
    "Minimum_x0020_Age": "Minimum Age",
    "Maximum_x0020_Age": "Maximum Age"
};
/**
 * Convert SharePoint list item to Excel row format
 * This allows reuse of existing createBaseTrial logic
 */
function convertSharePointToExcelRow(sharePointItem) {
    const excelRow = {};
    logger_1.logger.debug("Converting SharePoint item to Excel row format", "sharepoint-converter", {
        sharePointId: sharePointItem.Id,
        title: sharePointItem.Title
    });
    // Apply field mappings
    for (const [sharePointField, excelField] of Object.entries(sharePointToExcelMapping)) {
        const value = sharePointItem[sharePointField];
        if (value !== undefined && value !== null) {
            excelRow[excelField] = value;
            logger_1.logger.debug(`Mapped field: ${sharePointField} -> ${excelField}`, "sharepoint-converter", {
                value: typeof value === 'string' ? value.substring(0, 100) : value
            });
        }
    }
    // Handle special case for Protocol Abbrev - use Id if not mapped from Title
    if (!excelRow["Protocol Abbrev"] && sharePointItem.Id) {
        excelRow["Protocol Abbrev"] = `SP-${sharePointItem.Id}`;
    }
    // Ensure required fields have fallback values
    if (!excelRow["Title (full)"] && sharePointItem.Title) {
        excelRow["Title (full)"] = sharePointItem.Title;
    }
    logger_1.logger.debug("SharePoint to Excel conversion completed", "sharepoint-converter", {
        sharePointId: sharePointItem.Id,
        excelFields: Object.keys(excelRow).length,
        mappedFields: Object.keys(excelRow)
    });
    return excelRow;
}
/**
 * Convert multiple SharePoint items to Excel row format
 */
function convertSharePointItemsToExcelRows(sharePointItems) {
    logger_1.logger.info(`Converting ${sharePointItems.length} SharePoint items to Excel format`, "sharepoint-converter");
    const excelRows = sharePointItems.map(item => convertSharePointToExcelRow(item));
    logger_1.logger.info(`Successfully converted ${excelRows.length} SharePoint items to Excel format`, "sharepoint-converter");
    return excelRows;
}
/**
 * Get available SharePoint fields from an item (for debugging)
 */
function getAvailableSharePointFields(sharePointItem) {
    return Object.keys(sharePointItem).sort();
}
/**
 * Get the Excel field mapping for a SharePoint field
 */
function getExcelFieldForSharePointField(sharePointField) {
    return sharePointToExcelMapping[sharePointField];
}
/**
 * Get all SharePoint field mappings
 */
function getAllFieldMappings() {
    return { ...sharePointToExcelMapping };
}
/**
 * Validate that a SharePoint item has the minimum required fields
 */
function validateSharePointItem(sharePointItem) {
    const errors = [];
    // Check required fields
    if (!sharePointItem.Id) {
        errors.push("Missing required field: Id");
    }
    if (!sharePointItem.Title) {
        errors.push("Missing required field: Title");
    }
    return {
        valid: errors.length === 0,
        errors
    };
}
/**
 * Log field mapping summary for debugging
 */
function logFieldMappingSummary(sharePointItem) {
    const availableFields = getAvailableSharePointFields(sharePointItem);
    const mappedFields = availableFields.filter(field => sharePointToExcelMapping[field]);
    const unmappedFields = availableFields.filter(field => !sharePointToExcelMapping[field]);
    logger_1.logger.info("SharePoint field mapping summary", "sharepoint-converter", {
        totalFields: availableFields.length,
        mappedFields: mappedFields.length,
        unmappedFields: unmappedFields.length,
        mapped: mappedFields,
        unmapped: unmappedFields
    });
}
